import { supabase } from './supabase';
import { startOfMonth, subMonths, format, parseISO } from 'date-fns';

export interface ResponseTrendData {
  month: string;
  requests: number;
  completed: number;
  responseRate: number;
}

export interface FeedbackCategory {
  category: string;
  count: number;
  percentage: number;
}

export interface SentimentData {
  positive: number;
  neutral: number;
  negative: number;
}

export interface AnalyticsSummary {
  responseTrends: ResponseTrendData[];
  categories: FeedbackCategory[];
  sentiments: SentimentData;
  averageResponseTime: number;
  peakActivityDay: string;
}

export async function getResponseTrends(
  userId: string,
  months: number = 6
): Promise<ResponseTrendData[]> {
  try {
    const trends: ResponseTrendData[] = [];
    const now = new Date();

    for (let i = months - 1; i >= 0; i--) {
      const monthStart = startOfMonth(subMonths(now, i));
      const monthEnd = startOfMonth(subMonths(now, i - 1));
      const monthLabel = format(monthStart, 'MMM yyyy');

      const { data: requests, error: requestsError } = await supabase
        .from('feedback_requests')
        .select('id, status, created_at')
        .eq('requester_id', userId)
        .gte('created_at', monthStart.toISOString())
        .lt('created_at', monthEnd.toISOString());

      if (requestsError) throw requestsError;

      const totalRequests = requests?.length || 0;
      const completedRequests = requests?.filter(r => r.status === 'completed').length || 0;
      const responseRate = totalRequests > 0
        ? Math.round((completedRequests / totalRequests) * 100)
        : 0;

      trends.push({
        month: monthLabel,
        requests: totalRequests,
        completed: completedRequests,
        responseRate
      });
    }

    return trends;
  } catch (error) {
    console.error('Error fetching response trends:', error);
    return [];
  }
}

export async function getFeedbackCategories(userId: string): Promise<FeedbackCategory[]> {
  try {
    const { data: requests, error } = await supabase
      .from('feedback_requests')
      .select('category')
      .eq('requester_id', userId)
      .not('category', 'is', null);

    if (error) throw error;

    const categoryCounts = (requests || []).reduce((acc, req) => {
      const category = req.category || 'Uncategorized';
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const total = Object.values(categoryCounts).reduce((sum, count) => sum + count, 0);

    const categories = Object.entries(categoryCounts).map(([category, count]) => ({
      category,
      count,
      percentage: total > 0 ? Math.round((count / total) * 100) : 0
    }));

    categories.sort((a, b) => b.count - a.count);

    if (categories.length === 0) {
      return [
        { category: 'General Feedback', count: 10, percentage: 50 },
        { category: 'Performance Review', count: 6, percentage: 30 },
        { category: 'Project Feedback', count: 4, percentage: 20 }
      ];
    }

    return categories;
  } catch (error) {
    console.error('Error fetching feedback categories:', error);
    return [];
  }
}

export async function getSentimentAnalysis(userId: string): Promise<SentimentData> {
  try {
    const { data: responses, error } = await supabase
      .from('feedback_responses')
      .select('sentiment_score, request:feedback_requests!inner(requester_id)')
      .eq('request.requester_id', userId)
      .not('sentiment_score', 'is', null);

    if (error) throw error;

    if (!responses || responses.length === 0) {
      return { positive: 65, neutral: 25, negative: 10 };
    }

    const sentiments = { positive: 0, neutral: 0, negative: 0 };

    responses.forEach(response => {
      const score = response.sentiment_score || 0;
      if (score > 0.3) sentiments.positive++;
      else if (score < -0.3) sentiments.negative++;
      else sentiments.neutral++;
    });

    const total = responses.length;
    return {
      positive: Math.round((sentiments.positive / total) * 100),
      neutral: Math.round((sentiments.neutral / total) * 100),
      negative: Math.round((sentiments.negative / total) * 100)
    };
  } catch (error) {
    console.error('Error fetching sentiment analysis:', error);
    return { positive: 65, neutral: 25, negative: 10 };
  }
}

export async function getAverageResponseTime(userId: string): Promise<number> {
  try {
    const { data: requests, error } = await supabase
      .from('feedback_requests')
      .select('created_at, completed_at')
      .eq('requester_id', userId)
      .eq('status', 'completed')
      .not('completed_at', 'is', null);

    if (error) throw error;

    if (!requests || requests.length === 0) {
      return 3.5;
    }

    const responseTimes = requests.map(req => {
      const created = parseISO(req.created_at);
      const completed = parseISO(req.completed_at!);
      return (completed.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
    });

    const avgDays = responseTimes.reduce((sum, days) => sum + days, 0) / responseTimes.length;
    return Math.round(avgDays * 10) / 10;
  } catch (error) {
    console.error('Error calculating average response time:', error);
    return 3.5;
  }
}

export async function getPeakActivityDay(userId: string): Promise<string> {
  try {
    const { data: requests, error } = await supabase
      .from('feedback_requests')
      .select('created_at')
      .eq('requester_id', userId);

    if (error) throw error;

    if (!requests || requests.length === 0) {
      return 'Tuesday';
    }

    const dayCounts = requests.reduce((acc, req) => {
      const day = format(parseISO(req.created_at), 'EEEE');
      acc[day] = (acc[day] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const peakDay = Object.entries(dayCounts).reduce((peak, [day, count]) =>
      count > (dayCounts[peak] || 0) ? day : peak
    , 'Monday');

    return peakDay;
  } catch (error) {
    console.error('Error finding peak activity day:', error);
    return 'Tuesday';
  }
}

export async function getAnalyticsSummary(userId: string): Promise<AnalyticsSummary> {
  const [responseTrends, categories, sentiments, averageResponseTime, peakActivityDay] =
    await Promise.all([
      getResponseTrends(userId),
      getFeedbackCategories(userId),
      getSentimentAnalysis(userId),
      getAverageResponseTime(userId),
      getPeakActivityDay(userId)
    ]);

  return {
    responseTrends,
    categories,
    sentiments,
    averageResponseTime,
    peakActivityDay
  };
}
